import React, { useState, useRef, useEffect } from "react";
import "./card.css";
import { Box, Grid, Typography, Tooltip, Button, Zoom, Card, CardContent, CardMedia, Stack, Chip, IconButton, Badge } from "@mui/material";
import { GetTimeString } from "../../utiles";
import Countdown from "react-countdown";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import GavelIcon from '@mui/icons-material/Gavel';
import DiamondIcon from '@mui/icons-material/Diamond';
import { on } from "events";

export interface URIProp {
  uri: any;
  name: string;
  assetID: string;
  stakedTime?: any;
  onClick?: any;
  isSelected?: boolean;
  onClaim?: any;
  onStake?: any;
  onUpgrade?: any;
  nftData?: any;
}

const NFTCard = ({
  uri,
  nftData,
  name,
  assetID,
  stakedTime,
  isSelected,
  onClick,
  onClaim = null,
  onStake = null,
  onUpgrade = null,
}: URIProp) => {
  // State for managing hover and tooltip
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const cardRef = useRef(null);

  // Handle NFT card click
  const onClickNFT = (id: any) => {
    if (onClick) onClick(id);
  };

  // Show tooltip when hovered for more than 500ms
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isHovered) {
      timeoutId = setTimeout(() => {
        setTooltipOpen(true);
      }, 500);
    } else {
      setTooltipOpen(false);
    }

    return () => clearTimeout(timeoutId);
  }, [isHovered]);

  // Handle mouse enter and leave for hover state
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const getToolStatus = () => {
    const currentUnixTime = Math.floor(Date.now() / 1000);
    if (nftData?.upgrade_end_at > currentUnixTime)
      return "Upgrading";
    else
      return "Ready";
  };

  // Function to render attribute items in the tooltip
  const renderAttributeItem = (label: string, value: any) => {
    if (!value) return null;
    return (
      <Typography
        variant="body2"
        sx={{
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid #616161",
          pb: 0.5,
          mb: 0.5,
        }}
      >
        <span style={{ color: "#ccc" }}> {label}: </span>
        <span> {value} </span>
      </Typography>
    );
  };

  // Calculate if the tool is ready to claim
  const isClaimable = nftData?.last_claimed && nftData.last_claimed <= Math.floor(Date.now() / 1000);
  const isStaked = Boolean(nftData?.exploration_time);

  // Get tool reward info
  // Get reward amount based on current level from level_reward array
  const currentLevel = parseInt(nftData?.level) || 1;
  const levelRewards = nftData?.tool_config?.level_reward ?? [];
  const rewardAmount =
    Array.isArray(levelRewards) && levelRewards.length >= currentLevel
      ? parseFloat(levelRewards[currentLevel - 1]?.value?.split(" ")[0])
      : nftData?.tool_config?.reward_token
      ? parseFloat(nftData.tool_config.reward_token.split(" ")[0])
      : 0;
  
  const rewardToken = nftData?.tool_config?.reward_token
    ? nftData.tool_config.reward_token.split(" ")[1]
    : "";

  // Check if tool can be upgraded
  // console.log(nftData);
  const level_costa = nftData?.tool_config?.level_costa ?? [];
  const level_costb = nftData?.tool_config?.level_costb ?? [];

  const canUpgrade =
    Array.isArray(level_costa) &&
    Array.isArray(level_costb) &&
    level_costa.length > currentLevel &&
    level_costb.length > currentLevel;
  // const canUpgrade = true;

  const toolstatus = getToolStatus();
  return (
    <Grid item xs={1} sm={1} md={1} lg={1} xl={1}>
      <Tooltip
        PopperProps={{
          disablePortal: true,
        }}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "#1a203c",
              "& .MuiTooltip-arrow": {
                color: "#1a203c",
              },
              p: 2,
              maxWidth: "300px",
              maxHeight: "400px",
              overflowY: "auto",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "8px"
            },
          },
        }}
        open={tooltipOpen}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        placement="right-start"
        TransitionComponent={Zoom}
        title={
          <Box>
            {/* Render attributes */}
            {renderAttributeItem("Level", nftData?.level)}
            {renderAttributeItem("Asset ID", nftData?.asset_id)}
            {renderAttributeItem("Base", nftData?.data?.Base)}
            {renderAttributeItem("Eyes", nftData?.data?.Eyes)}
            {renderAttributeItem("Cloth", nftData?.data?.Cloth)}
            {renderAttributeItem("Horns", nftData?.data?.Horns)}
            {renderAttributeItem("Mouth", nftData?.data?.Mouth)}
            {renderAttributeItem("Wings", nftData?.data?.Wings)}
            {renderAttributeItem("Earring", nftData?.data?.Earring)}
            {renderAttributeItem("Glasses", nftData?.data?.Glasses)}
            {renderAttributeItem("Necklace", nftData?.data?.Necklace)}
            {renderAttributeItem("Background", nftData?.data?.Background)}
            {renderAttributeItem("Rewards", rewardAmount + " " + rewardToken + " / DAY")}
          </Box>
        }
      >
        <Card
          className="nft-card"
          ref={cardRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => onClickNFT(assetID)}
          sx={{
            background: 'linear-gradient(135deg, #7d5014 0%, #5c3c10 100%)',
            border: isSelected ? '3px solid #d9534f' : '3px solid #5c3c10',
            borderRadius: '12px',
            position: 'relative',
            overflow: 'hidden',
            cursor: "pointer",
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              borderColor: '#d9534f',
              transform: 'translateY(-5px)',
              boxShadow: '0 10px 20px rgba(0, 0, 0, 0.4)'
            },
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)'
          }}
        >
          {/* Dark overlay for better readability */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 0
          }} />

          {/* Status Chip */}
          {isStaked && (
            <Chip
              label={isClaimable ? toolstatus : "Staked"}
              size="small"
              color={
                toolstatus === "Upgrading"
                  ? "warning"
                  : isClaimable
                  ? "error"
                  : "success"
              }
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 2,
                fontSize: '0.7rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            />
          )}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              bgcolor: 'rgba(0,0,0,0.7)',
              borderRadius: '50%',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #d9a758',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            <Typography variant="caption" sx={{ color: '#d9a758', fontWeight: 'bold' }}>
              {nftData?.level || 1}
            </Typography>
          </Box>

          {/* NFT Image */}
          <CardMedia
            component="img"
            image={uri}
            alt={name}
            sx={{
              p: 2,
              aspectRatio: '1/1',
              objectFit: 'contain',
              transition: 'transform 0.3s ease',
              transform: isHovered ? 'scale(1.05)' : 'scale(1)',
              position: 'relative',
              zIndex: 1
            }}
          />

          {/* NFT Info */}
          <CardContent
            sx={{
              p: 2,
              pb: 2,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              bgcolor: 'rgba(0,0,0,0.6)',
              borderTop: '2px solid #5c3c10',
              position: 'relative',
              zIndex: 1
            }}
          >
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: 'white',
                  fontWeight: 'medium',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mb: 0.5
                }}
              >
                {name}
              </Typography>

              {nftData?.tool_config && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    display: 'block',
                    textAlign: 'center',
                    mb: 1
                  }}
                >
                  {rewardAmount} {rewardToken}/DAY
                </Typography>
              )}
            </Box>

            {/* Countdown for staked tools */}
            {nftData?.last_claimed && nftData.last_claimed > Math.floor(Date.now() / 1000) && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                  Ready in:
                </Typography>
                <Typography variant="body2" sx={{ color: '#e94c58', fontWeight: 'medium' }}>
                  <Countdown date={nftData.last_claimed * 1000} />
                </Typography>
              </Box>
            )}

            {/* Action Buttons */}
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 1 }}
            >
              {/* Stake Button */}
              {onStake && !isStaked ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStake();
                  }}
                  startIcon={<GavelIcon />}
                  className="action-button"
                  sx={{
                    bgcolor: '#2eca6a',
                    '&:hover': { bgcolor: '#25a65a' },
                    borderRadius: '6px',
                    fontWeight: 'medium',
                    fontSize: '0.75rem',
                    py: 0.5
                  }}
                >
                  Stake
                </Button>
              ) :
                <Stack
                  direction="column"
                  spacing={1}
                  justifyContent="center"
                  sx={{ mt: 1 }}
                >
                  {/* Claim Button */}
                  {onClaim && isClaimable && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClaim();
                      }}
                      startIcon={<DiamondIcon />}
                      className="action-button"
                      sx={{
                        bgcolor: '#e94c58',
                        '&:hover': { bgcolor: '#d43545' },
                        borderRadius: '6px',
                        fontWeight: 'medium',
                        fontSize: '0.75rem',
                        py: 0.5
                      }}
                    >
                      Claim
                    </Button>
                  )}
                  {onUpgrade && canUpgrade && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpgrade();
                      }}
                      startIcon={<UpgradeIcon />}
                      className="action-button"
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.3)',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.05)'
                        },
                        borderRadius: '6px',
                        fontWeight: 'medium',
                        fontSize: '0.75rem',
                        py: 0.5,
                        opacity: parseInt(nftData.level) === 5 || getToolStatus() === "Upgrading" ? 0.6 : 1,
                        backgroundColor: parseInt(nftData.level) === 5 ? "#bdbdbd" : undefined,
                        pointerEvents: parseInt(nftData.level) === 5 || getToolStatus() === "Upgrading" ? "none" : "auto",
                      }}
                    >
                      {
                        getToolStatus() === "Upgrading" ? "Upgrading" : "Upgrade"
                      }
                    </Button>
                  )}
                </Stack>
              }
              <IconButton
                size="small"
                href={`https://wax.atomichub.io/explorer/asset/wax-mainnet/${assetID}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  '&:hover': {
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  },
                  border: '1px solid rgba(255,255,255,0.2)',
                  p: 0.5,
                  width: '2rem',
                  height: '2rem',
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Stack>
          </CardContent>
        </Card>
      </Tooltip>
    </Grid>
  );
};

export default NFTCard;
